/**
 * DV-002: Demo Page Selector Component
 * URL-driven dropdown selector with theme tokens
 */

import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useDemoSelection, type DemoPage } from './useDemoSelection';
import { cn } from '@/utils/cn';

export interface DemoPageSelectorProps {
  className?: string;
  'data-testid'?: string;
}

export function DemoPageSelector({ className, 'data-testid': testId = 'dv-page-select' }: DemoPageSelectorProps) {
  const { currentPage, setPage, availablePages } = useDemoSelection();
  const [showDropdown, setShowDropdown] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Outside click handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  // Keyboard navigation within dropdown
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!showDropdown) return;

      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          setShowDropdown(false);
          setFocusedIndex(-1);
          break;
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex(prev => (prev + 1) % availablePages.length);
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex(prev => prev <= 0 ? availablePages.length - 1 : prev - 1);
          break;
        case 'Enter':
          event.preventDefault();
          if (focusedIndex >= 0) {
            handlePageSelect(availablePages[focusedIndex]);
          }
          break;
        case 'Home':
          event.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          event.preventDefault();
          setFocusedIndex(availablePages.length - 1);
          break;
      }
    };

    if (showDropdown) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [showDropdown, focusedIndex, availablePages]);

  const handlePageSelect = (page: DemoPage) => {
    setPage(page.id);
    setShowDropdown(false);
    setFocusedIndex(-1);
  };

  // Reset focused index when dropdown opens
  useEffect(() => {
    if (showDropdown) {
      // Set focus to current page when dropdown opens
      const currentIndex = availablePages.findIndex(page => page.id === currentPage.id);
      setFocusedIndex(currentIndex);
    } else {
      setFocusedIndex(-1);
    }
  }, [showDropdown, availablePages, currentPage.id]);

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        data-testid={testId}
        aria-haspopup="listbox"
        aria-expanded={showDropdown}
        aria-label="Select demo page"
        className="flex items-center space-x-2 px-4 py-2 min-w-[200px] rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2"
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderColor: 'var(--border-default)',
          color: 'var(--fg-primary)',
          // Hover state
          ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
          ['--hover-border' as any]: 'var(--border-interactive)',
          // Focus state
          ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
          e.currentTarget.style.borderColor = 'var(--hover-border)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--bg-surface)';
          e.currentTarget.style.borderColor = 'var(--border-default)';
        }}
        onFocus={(e) => {
          e.currentTarget.style.boxShadow = 'var(--focus-ring)';
        }}
        onBlur={(e) => {
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <span
          className="font-mono text-sm flex-1 text-left"
          style={{ fontFamily: 'var(--font-family-mono)' }}
        >
          {currentPage.name}
        </span>
        <ChevronDown
          className={cn("w-4 h-4 transition-transform", showDropdown && "rotate-180")}
          style={{
            transitionDuration: 'var(--motion-duration-fast)',
            color: 'var(--fg-muted)'
          }}
        />
      </button>

      {showDropdown && (
        <div
          className="absolute top-full left-0 mt-2 w-80 rounded-lg border shadow-xl backdrop-blur-xl z-50"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            borderColor: 'var(--border-default)',
            boxShadow: 'var(--shadow-lg)',
          }}
          role="listbox"
          aria-label="Demo page options"
        >
          {availablePages.map((page, index) => {
            const isSelected = currentPage.id === page.id;
            const isFocused = focusedIndex === index;

            return (
              <button
                key={page.id}
                onClick={() => handlePageSelect(page)}
                role="option"
                aria-selected={isSelected}
                data-testid={`dv-page-option-${page.id}`}
                className="w-full text-left px-4 py-3 border-b last:border-b-0 transition-colors focus:outline-none"
                style={{
                  borderColor: 'var(--border-subtle)',
                  backgroundColor: isFocused
                    ? 'var(--bg-interactive-hover)'
                    : isSelected
                    ? 'var(--bg-interactive-active)'
                    : 'transparent',
                  color: isSelected || isFocused ? 'var(--accent)' : 'var(--fg-primary)',
                  outline: isFocused ? '2px solid var(--border-focus)' : 'none',
                  outlineOffset: '-2px',
                }}
                onMouseEnter={(e) => {
                  if (!isFocused && !isSelected) {
                    e.currentTarget.style.backgroundColor = 'var(--bg-interactive-hover)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isFocused) {
                    e.currentTarget.style.backgroundColor = isSelected
                      ? 'var(--bg-interactive-active)'
                      : 'transparent';
                  }
                }}
              >
                <div
                  className="font-mono text-sm font-medium"
                  style={{ fontFamily: 'var(--font-family-mono)' }}
                >
                  {page.name}
                </div>
                <div
                  className="text-xs mt-1"
                  style={{ color: 'var(--fg-muted)' }}
                >
                  {page.description}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}